<script setup lang="ts">
import { $multipleUpdateSignStatus } from '@/api/signs'
import { useSnackbarStore } from '@/store/snackbar'
import { $multipleUpdateLineItemStatuses } from '@/api/brandings'
import { $getCampaignWorkflowStatuses } from '@/api/workflow'
import { $multipleUpdateKopItemStatus } from '@/api/kop'
import { useWaitingRoomAutoSyncStore } from '@/store/waitingRoomAutoSync'

const snackbarStore = useSnackbarStore()
const waitingRoomAutoSyncStore = useWaitingRoomAutoSyncStore()

interface IProps {
  modelValue?: boolean,
  campaignType?: 'wayfinding' | 'branding' | 'kop',
  selectedCampaignId?: number | null,
  selectedItemIds?: number[],
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  campaignType: 'wayfinding',
  selectedCampaignId: null,
  selectedItemIds: () => ([]),
})

const emit = defineEmits(['update:modelValue', 'onSubmit', 'onClose'])

interface WorkflowStatus {
  id: number;
  name: string;
}

const selectedWorkflowStatusId = ref<number | null>(null)
const dialogLoading = ref<boolean>(false)
const statusesLoading = ref<boolean>(false)
const workflowStatuses = ref<WorkflowStatus[]>([])

const onSubmit = async () => {
  dialogLoading.value = true
  try {
    // KOP
    if (props.campaignType === 'kop') {
      await multipleStatusUpdateKopItems()
    }
    // Wayfinding
    else if (props.campaignType === 'wayfinding') {
      await multipleStatusUpdateSigns()
    }
    // Branding
    else {
      await multipleStatusUpdateLineItems()
    }

    // Emit onSubmit and close dialog on success
    emit('onSubmit')
    closeDialog()
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const multipleStatusUpdateKopItems = async () => {
  const payload = {
    item_status: selectedWorkflowStatusId.value,
    kop_item_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateKopItemStatus(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'KOP Item(s) status(es) updated successfully!'
    })
  } catch (e: any) {
    const mes = e?.response?.data?.message ? e?.response?.data?.message : (e?.response?.data ? JSON.stringify(e?.response?.data) : e?.message)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  }
}

const multipleStatusUpdateSigns = async () => {
  const payload = {
    sign_status: selectedWorkflowStatusId.value,
    sign_ids: props.selectedItemIds
  }
  try {
    waitingRoomAutoSyncStore.handleResponse(await $multipleUpdateSignStatus(payload))
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Sign(s) status(es) updated successfully!'
    })
  } catch (e: any) {
    const mes = e?.response?.data?.message ? e?.response?.data?.message : (e?.response?.data ? JSON.stringify(e?.response?.data) : e?.message)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  }
}

const multipleStatusUpdateLineItems = async () => {
  const payload = {
    status_id: selectedWorkflowStatusId.value,
    line_item_ids: props.selectedItemIds
  }
  try {
    waitingRoomAutoSyncStore.handleResponse(await $multipleUpdateLineItemStatuses(payload))
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) status(es) updated successfully!'
    })
  } catch (e: any) {
    const mes = e?.response?.data?.message ? e?.response?.data?.message : (e?.response?.data ? JSON.stringify(e?.response?.data) : e?.message)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  }
}

const getWorkflowStatuses = async () => {
  // Check if selectedCampaignId is not available, then return
  if (!props.selectedCampaignId) return

  statusesLoading.value = true
  try {
    const res = await $getCampaignWorkflowStatuses(props.selectedCampaignId)
    workflowStatuses.value = res.data
  } catch (e) {
    console.log(e)
  } finally {
    statusesLoading.value = false
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  selectedWorkflowStatusId.value = null
  emit('onClose')
}

watch(() => props.modelValue, () => {
  // on show Dialog
  if (!!props.modelValue) {
    getWorkflowStatuses()
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <div>
    <VDialog :model-value="modelValue" max-width="600" class="default-dialog" :persistent="!!dialogLoading"
      @after-leave="onClose" @update:modelValue="emit('update:modelValue')">
      <VBtn variant="elevated" density="compact" icon="tabler-x" class="v-dialog-close-btn" :disabled="!!dialogLoading"
        @click="closeDialog" />

      <VCard title="Update Status">
        <VCardText>
          <VRow>
            <VCol class="py-0">
              <VAutocomplete v-model="selectedWorkflowStatusId" :items="[...workflowStatuses]"
                :disabled="!!dialogLoading || !!statusesLoading" :loading="!!statusesLoading" item-title="name"
                item-value="id" density="compact" label="Status" no-data-text="Statuses not found" />
            </VCol>
          </VRow>
          <VRow>
            <VCol class="d-flex justify-end" cols="12">
              <VBtn :disabled="!!dialogLoading" @click="onSubmit">
                <VProgressCircular v-if="dialogLoading" class="mr-1" size="x-small" :width="2" indeterminate
                  color="light" />
                Update
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>
